import type { ReactNode } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { VariantProp } from '../../common/colors';
import type { StateIconProps } from '../../formatting/StateIcon/StateIcon';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { InternalProp } from '../../utilities/useInternal';
import type { AlertProps } from '../Alert/Alert';
import { Icon } from '../../assets/Icon/Icon';
import { getVariantStyles, variantBodyColor, variantHeadingColor } from '../../common/variant';
import { CopyBox } from '../../formatting/CopyBox/CopyBox';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { StateIcon } from '../../formatting/StateIcon/StateIcon';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body, BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';
import { toLowerCaseFirstCharacterSmartIsh } from '../../utilities/strings';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  UserAccessLevelContextProvider,
} from '../../utilities/useAccessLevelContext';
import { useAccessLevelOrInternal } from '../../utilities/useInternal';
import { Alert } from '../Alert/Alert';
import { LabelTooltip, Tooltip } from '../Tooltip/Tooltip';

type SummaryListSize = 'x-small' | 'medium';

export type SummaryListPairState = StateIconProps & {
  /**
   * Extra context shown after the icon, such as a mode or threshold.
   */
  detail?: React.ReactNode;
};

type SummaryListValuePair = {
  /**
   * The value for the key.
   */
  value: React.ReactNode;
  state?: never;
};

type SummaryListStatePair = {
  value?: never;
  /**
   * Shows an enabled or disabled state as the pair's value, in place of `value`. Pass a boolean, or
   * an object to add a detail, tone or screen reader label.
   */
  state: boolean | SummaryListPairState;
};

export type SummaryListPairsProps = (SummaryListValuePair | SummaryListStatePair) & {
  /**
   * The label for the key.
   */
  label: React.ReactNode;
  /**
   * The width of the label.
   */
  labelWidth?: SpacingScale;
  /**
   * Access level for the pair.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Pass in an alert to display below contents of the row.
   */
  alert?: Omit<AlertProps, 'type'>;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Group identifier for organizing pairs.
   */
  group?: string;
  /**
   * Boolean to allow the value to be copied to clipboard.
   */
  copyable?:
    | boolean
    | {
        label: React.ReactNode;
        value?: string;
        rawValue?: string;
      };
  /**
   * Set the font family of the summary list.
   */
  monospaced?: boolean;
  /**
   * Provide a tooltip for the key.
   */
  tooltip?: React.ReactNode;
  /**
   * Set if the row has a special variant like `attention` or `negative` to highlight an issue.
   */
  variant?: VariantProp;
};

export type SummaryListGroupData = {
  /**
   * The label for the group.
   */
  label: React.ReactNode;
  /**
   * The pairs in this group.
   */
  pairs: SummaryListPairsProps[];
  /**
   * Boolean to show internal badge for the group.
   */
  internal?: boolean;
  /**
   * Set the size of the summary list for this group.
   */
  size?: SummaryListSize;
};

const summaryListContainerName = 'summary-list';
const summaryListContainers = {
  narrow: `@container ${summaryListContainerName} (max-width: 319px)`,
};

// Built on `Text` rather than `Small`, whose base colour would override `$$keyColor` in dark mode.
const SummaryListKeyLabelSmall = styled(Text, {
  ...SmallSansSizes,
  color: '$$keyColor',
  fontWeight: '$bold',
});
SummaryListKeyLabelSmall.displayName = 'SummaryListKeyLabelSmall';

const SummaryListKeyLabel = styled(Text, {
  color: '$$keyColor',
  fontWeight: '$bold',
});
SummaryListKeyLabel.displayName = 'SummaryListKeyLabel';

// Paints over the hairline behind the key, leaving a gap after each line of text.
const SummaryListKeyMask = styled('span', {
  position: 'relative',
  paddingRight: '$8',
  backgroundColor: '$$keyMaskColor',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
});
SummaryListKeyMask.displayName = 'SummaryListKeyMask';

// Flows the tooltip icon and access badge after the key's last word, inside its mask.
const SummaryListKeyAccessory = styled('span', {
  display: 'inline-flex',
  verticalAlign: 'middle',
  marginLeft: '$4',
});
SummaryListKeyAccessory.displayName = 'SummaryListKeyAccessory';

const SummaryListKeyContainer = styled('div', {
  display: 'flex',
  alignItems: 'baseline',

  '& > *:first-child': {
    minWidth: 0,
  },

  variants: {
    hairline: {
      // Runs the full width of the key's first line, behind the key's masked text, so it shows
      // from the end of the text even when a wrapped key leaves its box wider than the text.
      true: {
        ...SmallSansSizes,
        position: 'relative',

        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 0 auto',
          height: '1lh',
          backgroundImage: 'linear-gradient($$variantStrokeColor, $$variantStrokeColor)',
          backgroundSize: '100% $space$1',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',

          [summaryListContainers.narrow]: {
            display: 'none',
          },
        },
      },
      false: {},
    },
  },
});
SummaryListKeyContainer.displayName = 'SummaryListKeyContainer';

const SummaryListValueContainer = styled(Text, {
  display: 'flex',
  width: '100%',
  maxWidth: '$700',
  wordBreak: 'break-word',
  color: '$$valueColor',

  variants: {
    monospaced: {
      true: {
        fontFamily: '$mono',
      },
      false: {
        fontFamily: '$sans',
      },
    },
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
    },
  },
});
SummaryListValueContainer.displayName = 'SummaryListValueContainer';

const SummaryListRowContainer = styled(AlignStack, {
  ...getVariantStyles('neutral'),
  // Padding offset by negative margins, so the hover highlight grows into the row gap without
  // moving anything.
  width: 'calc(100% + $space$16)',
  margin: '-$4 -$8',
  padding: '$4 $8',
  borderRadius: '$6',
  $$keyColor: variantBodyColor,
  $$valueColor: variantHeadingColor,
  // The key's mask has to match the surface behind the row. Set `$$summaryListSurface` on an
  // ancestor that isn't `bgApplication`; Stitches compiles `$$name` to `---name`, which can't be
  // written with a fallback any other way. Nested under `&` so its dark block doesn't replace the
  // one spread in from `getVariantStyles`.
  '&': {
    $$keyMaskColor: `var(---summaryListSurface, ${colors.bgApplicationLight})`,

    [darkThemeSelector]: {
      $$keyMaskColor: `var(---summaryListSurface, ${colors.bgApplicationDark})`,
    },
  },

  '&:hover': {
    $$keyMaskColor: colors.bgSurfaceLight,
    backgroundColor: '$$keyMaskColor',

    [darkThemeSelector]: {
      $$keyMaskColor: colors.bgSurfaceDark,
    },
  },

  // AlignStack locks its start slot to one centred line; size it to the key instead so a wrapped
  // key fits and the row can align on the key's baseline.
  '&& > *:first-child': {
    height: 'auto',
    minHeight: 0,
    maxHeight: 'none',
    alignItems: 'flex-start',
  },

  // In narrow containers, collapse from row to column. The doubled selector (&&) gives this
  // two-class specificity (0,2,0) vs AlignStack's single-class direction variant (0,1,0), so
  // it wins regardless of stylesheet injection order.
  [summaryListContainers.narrow]: {
    '&&': {
      flexDirection: 'column',
      gap: '$2',
    },
  },

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      internal: true,
      css: {
        ...getVariantStyles('neutral', true),
      },
    },
  ],
});
SummaryListRowContainer.displayName = 'SummaryListRowContainer';

const SummaryListRowWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
SummaryListRowWrapper.displayName = 'SummaryListRowWrapper';

// Inline rather than flex so the row has a text baseline to align to even when there's no detail.
const SummaryListStateValue = styled('span', {
  '& > :first-child': {
    marginRight: '$6',
  },
});
SummaryListStateValue.displayName = 'SummaryListStateValue';

function SummaryListState({ state }: { state: boolean | SummaryListPairState }) {
  const { detail, ...iconProps }: SummaryListPairState =
    typeof state === 'boolean' ? { enabled: state } : state;

  return (
    <SummaryListStateValue>
      <StateIcon {...iconProps} />
      {detail != null && detail !== false && detail !== '' && detail}
    </SummaryListStateValue>
  );
}

const getCopyable = (pair: SummaryListPairsProps) =>
  typeof pair.copyable === 'object'
    ? {
        label:
          pair.copyable?.label ||
          `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.copyable?.value || pair.value?.toString(),
        rawValue: pair.copyable?.rawValue,
      }
    : {
        label: `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.value?.toString(),
      };

function SummaryListPairs({
  accessLevel: accessLevelProp,
  internal,
  pairs,
  size,
  direction = 'row',
}: {
  accessLevel?: AccessLevelProp;
  internal?: InternalProp;
  pairs: SummaryListPairsProps[];
  size: SummaryListSize;
  direction?: 'column' | 'row';
}) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  if (pairs) {
    return pairs
      .filter(
        (pair) =>
          pair.label != null &&
          (pair.value != null || pair.state != null) &&
          pair.accessLevel !== null,
      )
      .map((pair, index) => {
        const key = index;
        const labelWidthCSS =
          direction === 'row'
            ? merge(widthCSS(pair.labelWidth ?? 176), {
                [summaryListContainers.narrow]: { width: 'auto' },
              })
            : undefined;

        // Calculate access level for this pair
        let pairAccessLevel: AccessLevel | undefined;
        if (pair.accessLevel) {
          pairAccessLevel = pair.accessLevel as AccessLevel;
        } else if (pair.internal) {
          pairAccessLevel = AccessLevel.Internal;
        } else if (accessLevel) {
          pairAccessLevel = accessLevel as AccessLevel;
        }

        const badge = pairAccessLevel && isAccessLevelVisible(pairAccessLevel) && (
          <FeatureBadge arrangement="icon-only" type={pairAccessLevel} size="x-small" />
        );

        let keyElement: ReactNode;
        if (direction === 'row') {
          const keyLabel = (
            <SummaryListKeyLabelSmall>
              <SummaryListKeyMask>
                {pair.label}
                {pair.tooltip && (
                  <SummaryListKeyAccessory>
                    <Icon icon="question" size={14} variant="neutral" />
                  </SummaryListKeyAccessory>
                )}
                {badge && <SummaryListKeyAccessory>{badge}</SummaryListKeyAccessory>}
              </SummaryListKeyMask>
            </SummaryListKeyLabelSmall>
          );
          keyElement = (
            <SummaryListKeyContainer css={labelWidthCSS} hairline>
              {pair.tooltip ? <Tooltip contents={pair.tooltip}>{keyLabel}</Tooltip> : keyLabel}
            </SummaryListKeyContainer>
          );
        } else {
          const keyLabel = <SummaryListKeyLabel>{pair.label}</SummaryListKeyLabel>;
          keyElement = (
            <SummaryListKeyContainer css={labelWidthCSS}>
              <AlignStack align="start" gap={4} preset="small" width="fit-content" end={badge}>
                {pair.tooltip ? (
                  <LabelTooltip contents={pair.tooltip} preset="small" truncate={false}>
                    {keyLabel}
                  </LabelTooltip>
                ) : (
                  keyLabel
                )}
              </AlignStack>
            </SummaryListKeyContainer>
          );
        }

        // Render value with copyable functionality
        const copyableData = pair.copyable && getCopyable(pair);
        let valueElement: ReactNode;
        if (pair.state != null) {
          valueElement = <SummaryListState state={pair.state} />;
        } else if (pair.value != null) {
          if (copyableData) {
            valueElement = (
              <CopyBox
                aria-label={copyableData.label}
                value={
                  copyableData.rawValue ??
                  copyableData.value ??
                  (pair.value != null ? pair.value : undefined)
                }
              >
                {pair.value}
              </CopyBox>
            );
          } else {
            valueElement = pair.value;
          }
        } else {
          valueElement = <>&mdash;</>;
        }

        return (
          <UserAccessLevelContextProvider key={`summaryListEcho-${key}`} value={pairAccessLevel}>
            <SummaryListRowWrapper>
              <SummaryListRowContainer
                align={direction === 'row' ? 'baseline' : 'start'}
                direction={direction}
                gap={direction === 'row' ? 8 : 2}
                preset="body"
                start={keyElement}
                variant={pair.variant}
                internal={pairAccessLevel === AccessLevel.Internal}
              >
                <SummaryListValueContainer size={size} monospaced={pair.monospaced}>
                  {valueElement}
                </SummaryListValueContainer>
              </SummaryListRowContainer>
              {pair.alert ? <Alert {...pair.alert} type="inline" /> : null}
            </SummaryListRowWrapper>
          </UserAccessLevelContextProvider>
        );
      });
  }

  return null;
}

export function createSummaryListGroups(
  pairs: SummaryListPairsProps[],
  groupLabels: Record<string, React.ReactNode> = {},
): SummaryListGroupData[] {
  const groupsMap = new Map<string, SummaryListPairsProps[]>();

  // Group pairs by their group identifier
  pairs.forEach((pair) => {
    const groupKey = pair.group || 'default';
    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, []);
    }
    groupsMap.get(groupKey)!.push(pair);
  });

  // Convert to SummaryListGroupData format
  return Array.from(groupsMap.entries()).map(([groupKey, groupPairs]) => ({
    label: groupLabels[groupKey] || groupKey,
    pairs: groupPairs,
  }));
}

const SummaryListGroupLabel = styled(Body, {
  display: 'flex',
  alignItems: 'center',
  gap: '$12',
  fontWeight: '$bold',

  '&::after': {
    content: '',
    flex: 1,
    height: '$1',
    backgroundColor: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.strokeNeutralDark,
    },
  },
});
SummaryListGroupLabel.displayName = 'SummaryListGroupLabel';

const SummaryListGroupItems = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
SummaryListGroupItems.displayName = 'SummaryListGroupItems';

const SummaryListGroupContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
});
SummaryListGroupContainer.displayName = 'SummaryListGroupContainer';

export type SummaryListGroupProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Set the direction of the items in the list (only works if `pairs` is provided).
   */
  direction?: 'column' | 'row';
  /**
   * Set the gap between the children.
   */
  gap?: SpacingScale;
  /**
   * Boolean to show internal badge.
   */
  internal?: boolean;
  /**
   * Provide a label for the group with `label`.
   */
  label?: React.ReactNode;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: SummaryListPairsProps[];
  /**
   * Set the size of the summary list.
   */
  size?: SummaryListSize;
};

export function SummaryListGroup({
  children,
  direction,
  gap,
  internal,
  label,
  pairs,
  size = 'medium',
  ...remaining
}: SummaryListGroupProps) {
  const css = merge(spacingCSS(gap));

  return (
    <SummaryListGroupContainer {...remaining}>
      <SummaryListGroupLabel>{label}</SummaryListGroupLabel>
      <SummaryListGroupItems css={css}>
        {children}
        {pairs && (
          <SummaryListPairs direction={direction} internal={internal} pairs={pairs} size={size} />
        )}
      </SummaryListGroupItems>
    </SummaryListGroupContainer>
  );
}

function SummaryListGroups({
  direction,
  gap,
  groups,
  size,
}: {
  direction?: 'column' | 'row';
  gap?: SpacingScale;
  groups: SummaryListGroupData[];
  size: SummaryListSize;
}) {
  if (groups) {
    return groups.map((group) => (
      <SummaryListGroup
        key={`summaryListEchoGroup-${group.label}`}
        direction={direction}
        gap={gap}
        internal={group.internal}
        label={group.label}
        pairs={group.pairs}
        size={size}
      />
    ));
  }

  return null;
}

export const SummaryListContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  containerType: 'inline-size',
  containerName: summaryListContainerName,
});
SummaryListContainer.displayName = 'SummaryListContainer';

export type SummaryListProps = {
  /**
   * Set the direction of the items in the list (only works if `pairs` is provided).
   */
  direction?: 'column' | 'row';
  /**
   * Set the gap between the children.
   */
  gap?: SpacingScale;
  /**
   * Access level of the summary list.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: SummaryListPairsProps[];
  /**
   * Can pass in groups of pairs for organized display.
   */
  groups?: SummaryListGroupData[];
  /**
   * Set the size of the summary list.
   */
  size?: SummaryListSize;
};

export function SummaryList({
  accessLevel: accessLevelProp,
  direction = 'row',
  internal,
  gap,
  pairs,
  groups,
  size = 'medium',
  ...remaining
}: SummaryListProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  const hasGroups = groups && groups.length > 0;
  const hasPairs = pairs && pairs.length > 0;

  if (!hasGroups && !hasPairs) {
    return null;
  }

  let groupsToRender = groups;
  let willHaveGroups = hasGroups;
  if (hasPairs && !hasGroups) {
    const pairsWithGroups = pairs!.filter((pair) => pair.group);
    if (pairsWithGroups.length > 0) {
      groupsToRender = createSummaryListGroups(pairs!);
      willHaveGroups = true;
    }
  }

  const finalGap = gap ?? (direction === 'row' ? 8 : 12);
  const css = spacingCSS(willHaveGroups ? 12 : finalGap);

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <SummaryListContainer css={css} {...remaining}>
        {groupsToRender ? (
          <SummaryListGroups
            direction={direction}
            gap={finalGap}
            groups={groupsToRender}
            size={size}
          />
        ) : (
          <SummaryListPairs direction={direction} internal={internal} pairs={pairs!} size={size} />
        )}
      </SummaryListContainer>
    </UserAccessLevelContextProvider>
  );
}
